import React, { useMemo } from "react";
import { motion } from "framer-motion";

// Premium floating mini-flags overlay for the Hero
// Uses flagcdn.com (no API key, free)
const COUNTRIES = [
  { code: "us", name: "USA" },
  { code: "gb", name: "UK" },
  { code: "nl", name: "Netherlands" },
  { code: "ch", name: "Switzerland" },
  { code: "de", name: "Germany" },
  { code: "fr", name: "France" },
  { code: "it", name: "Italy" },
  { code: "es", name: "Spain" },
  { code: "ca", name: "Canada" },
  { code: "au", name: "Australia" },
  { code: "ie", name: "Ireland" },
  { code: "se", name: "Sweden" },
];

// Deterministic pseudo-random for stable SSR-ish positions per session
const rand = (seed, min, max) => {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  const v = x - Math.floor(x);
  return min + v * (max - min);
};

const FlyingFlags = () => {
  const flags = useMemo(
    () =>
      COUNTRIES.map((c, i) => ({
        ...c,
        // initial position in %
        x: rand(i + 1, 4, 92),
        y: rand(i + 11, 8, 88),
        // drift amplitudes
        dx: rand(i + 21, 30, 70) * (i % 2 === 0 ? 1 : -1),
        dy: rand(i + 31, 24, 48) * (i % 3 === 0 ? -1 : 1),
        // animation timing
        duration: rand(i + 41, 14, 24),
        delay: rand(i + 51, 0, 4),
        // size and rotation
        size: rand(i + 61, 22, 34), // px
        rot: rand(i + 71, -10, 10),
        opacity: rand(i + 81, 0.55, 0.95),
      })),
    []
  );

  return (
    <div
      className="pointer-events-none absolute inset-0 z-[1] overflow-hidden"
      aria-hidden="true"
      data-testid="flying-flags"
    >
      {flags.map((f, i) => (
        <motion.img
          key={f.code}
          src={`https://flagcdn.com/w80/${f.code}.png`}
          srcSet={`https://flagcdn.com/w40/${f.code}.png 1x, https://flagcdn.com/w80/${f.code}.png 2x`}
          alt={f.name}
          loading="lazy"
          draggable={false}
          style={{
            position: "absolute",
            left: `${f.x}%`,
            top: `${f.y}%`,
            width: `${f.size}px`,
            height: "auto",
            borderRadius: "3px",
            boxShadow:
              "0 6px 14px -6px rgba(8,15,32,0.35), 0 0 0 1px rgba(255,255,255,0.5)",
            willChange: "transform",
            opacity: f.opacity,
          }}
          initial={{ x: 0, y: 0, rotate: f.rot }}
          animate={{
            x: [0, f.dx, 0, -f.dx * 0.6, 0],
            y: [0, f.dy, f.dy * 0.4, -f.dy * 0.5, 0],
            rotate: [f.rot, f.rot + 6, f.rot - 4, f.rot + 3, f.rot],
          }}
          transition={{
            duration: f.duration,
            delay: f.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

export default FlyingFlags;
